import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import styled from 'styled-components';
import MergeIcon from '@mui/icons-material/MergeType';
import ReplaceIcon from '@mui/icons-material/SwapHoriz';

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background-color: #1e1e1e;
    color: white;
    min-width: 500px;

    @media (max-width: 600px) {
      min-width: 90vw;
    }
  }
`;

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  color: #aaa;
  transition: color 0.2s ease;
`;

const OptionTitle = styled(Typography)`
  font-weight: 600;
  font-size: 1.1rem;
  color: white;
  transition: color 0.2s ease;
`;

const OptionButton = styled(Button)`
  padding: 20px;
  margin: 10px 0;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  border: 2px solid #444;
  background-color: transparent;

  &:hover {
    background-color: rgba(33, 150, 243, 0.15);
    border-color: #2196f3;

    ${IconWrapper} {
      color: #2196f3;
    }

    ${OptionTitle} {
      color: #2196f3;
    }
  }

  &:active {
    background-color: rgba(33, 150, 243, 0.25);
  }
`;

const OptionDescription = styled(Typography)`
  font-size: 0.9rem;
  color: #999;
  margin-top: 4px;
`;

const MergeReplaceDialog = ({ open, onClose, onMerge, onReplace, fileName, isForeheadOnly }) => {
  const handleMergeClick = () => {
    onMerge();
    onClose();
  };

  const handleReplaceClick = () => {
    onReplace();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ color: 'white', borderBottom: '1px solid #333', pb: 2 }}>
        Import Draft: {fileName}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          {isForeheadOnly
            ? 'This file contains 15-slot hero data. Choose how to apply it:'
            : 'Choose how to import this draft file:'}
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <OptionButton
            variant="outlined"
            fullWidth
            onClick={handleMergeClick}
          >
            <IconWrapper>
              <MergeIcon fontSize="large" />
              <OptionTitle>
                {isForeheadOnly ? 'Copy to Current Draft' : 'Merge with Current Draft'}
              </OptionTitle>
            </IconWrapper>
            <OptionDescription>
              {isForeheadOnly ? (
                <><strong>Keep current scenarios + overwrite the 15 hero slots</strong> with the ones from this file.</>
              ) : (
                <><strong>Keep current work + Add imported scenarios.</strong>
                <br />
                Your current scenarios will be preserved, and imported scenarios will be added alongside them.
                Perfect for combining drafts from multiple sources.</>
              )}
            </OptionDescription>
          </OptionButton>

          <OptionButton
            variant="outlined"
            fullWidth
            onClick={handleReplaceClick}
          >
            <IconWrapper>
              <ReplaceIcon fontSize="large" />
              <OptionTitle>
                {isForeheadOnly ? 'Replace 15 Slots Only' : 'Replace Current Draft'}
              </OptionTitle>
            </IconWrapper>
            <OptionDescription>
              {isForeheadOnly ? (
                <><strong>Overwrite the 15 hero slots</strong> with the imported ones. Scenarios stay untouched.</>
              ) : (
                <><strong>Discard current work + Load imported draft.</strong>
                <br />
                Your current draft will be completely replaced with the imported file.
                ⚠️ Any unsaved changes will be lost - save first if needed!</>
              )}
            </OptionDescription>
          </OptionButton>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #333', pt: 2, px: 3, pb: 2, justifyContent: 'center' }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            color: '#999',
            borderColor: '#555',
            minWidth: '120px',
            '&:hover': {
              borderColor: '#777',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default MergeReplaceDialog;
